<template>
  <div class="tw-min-h-screenx">
    <!-- Clean Header Section -->
    <div class="tw-px-4 tw-py-4 tw-pt-0">
      <div class="tw-mx-auto">
        <!-- Page Title & Stats Row -->
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-6">
          <div class="tw-flex tw-items-center tw-gap-6">
            <div>
              <h1 class="tw-text-3xl tw-font-bold tw-text-gray-900">
                {{ $t('pages.community', 'Community') }}
              </h1>
              <p class="tw-text-gray-600 tw-mt-1">
                {{ $t('pages.community-description', 'Connect with professionals and industry experts') }}
              </p>
            </div>

          </div>
        </div>

        <!-- Search Bar -->
        <div class="tw-flex tw-items-center tw-gap-4">
          <!-- Search Input -->
          <div class="tw-flex-1 tw-max-w-2xl">
            <FormField 
              v-model="search" 
              type="text" 
              :hide-label="true" 
              :hide-errors="true" 
              :placeholder="$t('common.search-community', 'Search members, companies, roles...')"
              left-icon="solar:magnifer-linear" 
              :right-icon="loading ? 'eos-icons:loading': ''"
              @input="onSearch" 
              class="tw-bg-white"
               
            />
          </div>


        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class=" tw-mx-auto tw-px-4 tw-pb-8">
      <!-- Loading State -->
      <div v-if="loading && members.length === 0" 
           class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 2xl:tw-grid-cols-5 tw-gap-6">
        <div v-for="i in 20" :key="i" 
             class="tw-border tw-rounded-lg tw-overflow-hidden tw-bg-white tw-pb-6">
          <div class="tw-p-6 tw-text-center">
            <div class="tw-w-20 tw-h-20 tw-rounded-full tw-bg-gray-200 tw-animate-pulse tw-mx-auto tw-mb-4"></div>
            <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-mb-2"></div>
            <div class="tw-h-4 tw-bg-gray-100 tw-rounded tw-animate-pulse tw-w-3/4 tw-mx-auto"></div>
          </div>
        </div>
      </div>

      <!-- Members Display -->
      <div v-else-if="members.length > 0">
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 2xl:tw-grid-cols-5 tw-gap-6">
          <div v-for="member in members" :key="member.id">
            <MemberCard :member="member" class="tw-h-full" />
          </div>
        </div>
      </div>

             <!-- Empty State -->
       <div v-else class="tw-text-center tw-py-12 tw-min-h-[calc(100vh-400px)] tw-flex tw-items-center tw-justify-center">
         <div class="tw-max-w-md tw-mx-auto">
           <img :src="$frontend('assets/images/empty-state/community.svg')" class="tw-w-full tw-max-w-[200px] tw-h-full tw-object-cover tw-rounded-lg tw-mx-auto tw-mb-4" />
           <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">
             {{ $t('messages.no-community-found', 'We couldn\'t find anyone in the community') }}
           </h3>
           <p class="tw-text-gray-500 tw-mb-4">
             {{ $t('messages.no-community-description', 'Try adjusting your filters or search terms to discover more people, buyers, and professionals on the platform.') }}
           </p>
           <p class="tw-text-gray-400 tw-text-sm">
             {{ $t('messages.no-community-encouragement', 'Still nothing? New members join every day — check back soon!') }}
           </p>
         </div>
       </div>
    </div>
  </div>
</template>

<script setup>
  import MemberCard from '@/components/dashboard/community/MemberCard.vue';
  import FormField from '@/components/form/FormField.vue';
  import { onMounted, ref, onUnmounted } from 'vue';
  import communityApi from '@/api/community';
  const loading = ref(true);
  const members = ref([]);
  const search = ref('');

  const options = ref({
    current_page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
    from: 0,
  });

  // Search timeout for debouncing
  let searchTimeout = null;


  const getData = async (page = 1) => {
    loading.value = true;

    try {
      const params = {
        page,
        per_page: options.value.per_page,
        search: search.value.trim(),
      };

      // Remove empty parameters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      await communityApi.paginate(params)
      .then((response) => {
        const { data, ...rest } = response.data;
        members.value = data;
        options.value = rest;
      });

    } catch (error) {
      console.error('Error fetching community members:', error);
    } finally {
      loading.value = false;
    }
  };

  const onSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      options.value.current_page = 1;
      getData(1);
    }, 500);
  };

  onMounted(async () => {
    await getData();
  });

  onUnmounted(() => {
    clearTimeout(searchTimeout);
  });
</script>

<style scoped>
/* Minimal clean layout styles */
</style>